import { MarketType } from "../../../types";
import openDownloadPage from "./openDownloadPage";

/**
 * Detect iOS devices (including iPadOS 13+).
 */
function isIOSDevice(userAgent: string) {
  const isIOSUA = /iPad|iPhone|iPod/i.test(userAgent);

  // iPadOS 13+ may report as MacIntel with touch support
  const isIPadOS13Plus =
    navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1;

  return (isIOSUA || isIPadOS13Plus) && !(window as any).MSStream;
}

/**
 * Detect Android devices.
 */
function isAndroidDevice(userAgent: string) {
  return /android/i.test(userAgent);
}

/**
 * Attempts to open the mobile app via Universal/App Link.
 *
 * If the app does not open within the specified delay,
 * the user will be redirected to the download page.
 */
export default function openApp(market: MarketType) {
  const userAgent = navigator.userAgent;
  const isIOS = isIOSDevice(userAgent);
  const isAndroid = isAndroidDevice(userAgent);
  const deepLinkUrl = "https://deeplink.worldfamilyenglish.com/app/HomeStack";
  const delayMs = 2500;
  let didLeavePage = false;
  let finished = false;
  let timeout: ReturnType<typeof setTimeout> | undefined;

  /**
   * Removes all listeners and clears pending timeout.
   */
  const cleanup = () => {
    window.removeEventListener("blur", onLeave);
    window.removeEventListener("pagehide", onLeave);
    document.removeEventListener("visibilitychange", onVisibilityChange);

    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
    }
  };

  /**
   * Ensures cleanup runs only once.
   */
  const finish = () => {
    if (finished) return;

    finished = true;
    cleanup();
  };

  /**
   * Triggered when the page loses focus
   * (commonly when the app is successfully opened).
   */
  const onLeave = () => {
    didLeavePage = true;
    finish();
  };

  /**
   * Triggered when document visibility changes.
   * If the page becomes hidden, assume the app was opened.
   */
  const onVisibilityChange = () => {
    if (document.hidden) onLeave();
  };

  if (!isIOS && !isAndroid) return;

  window.addEventListener("blur", onLeave);
  window.addEventListener("pagehide", onLeave);
  document.addEventListener("visibilitychange", onVisibilityChange);

  timeout = setTimeout(() => {
    if (!didLeavePage) {
      finish();
      openDownloadPage(market);

      return;
    }

    finish();
  }, delayMs);

  // Attempt to open the app
  window.location.href = deepLinkUrl;
}
